import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import type { NotificationEventFrame, ErrorEventFrame, PushFrameMeta } from '@localagent/sdk';
import { usePushFrameSubscription } from '../../hooks/usePushFrameSubscription';
import { WORK_STATE_ORIGIN } from '../../hooks/useWorkManagement';
import {
  notificationMutePrefsAtom,
  isNotificationMuted,
} from '../../stores/notifications/atoms';
import { liveFeedHandleAtom } from '../../stores/sdk/live-feed-atom';
import { resolveErrorMessage } from '../errors/error-messages';
import { showNotificationToast, showErrorToast } from './notification-toasts';

/**
 * App-wide notification toast dispatcher. Mounted globally at
 * `MainLayout` (NOT per-view) so a new push frame raises a `sonner` toast from
 * ANY view — e.g. while the user is in a Chat session.
 *
 * Honors DISPLAY-time mute: a muted kind/origin is still DELIVERED
 * and stored (mute is read-time, events ADR-006) but NEVER toasts. Errors honor an
 * origin-level mute only (they carry severity, not a kind).
 *
 * Renders nothing — it is a pure side-effect subscriber, mounted alongside
 * `useNotificationStream` (which feeds the stores). Both register their own
 * handler set on the SAME bridge connection.
 */
export function NotificationToastDispatcher(): null {
  const mutePrefs = useAtomValue(notificationMutePrefsAtom);
  const liveFeed = useAtomValue(liveFeedHandleAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (!liveFeed) return;
    return liveFeed.onNotice((post) => {
      showNotificationToast({
        kind: 'info',
        title: post.poster_display_name,
        body: post.body,
        action: {
          label: 'Open',
          onClick: () => {
            navigate(`/feed/${post.id}`);
          },
        },
      });
    });
  }, [liveFeed, navigate]);

  const onNotification = useCallback(
    (frame: NotificationEventFrame, meta: PushFrameMeta) => {
      // Only LIVE events toast. Replayed (historical) frames drained on
      // (re)connect fill the list/store but must NOT toast — replaying a whole
      // account history as toasts spams the user and overloads sonner
      // ("Maximum update depth exceeded"). They still reach the store via
      // useNotificationStream, which consumes both live and replay.
      if (meta.replay) return;
      // A ticket lane change is a refresh signal, not an announcement: the
      // `/work` catalog holder moves the board and this origin toasts nothing,
      // so a project run stepping tickets raises no toast per transition.
      if (frame.service_origin === WORK_STATE_ORIGIN) return;
      if (isNotificationMuted({ kind: frame.kind, serviceOrigin: frame.service_origin }, mutePrefs)) {
        return; // display-time mute — delivered + stored, but no toast
      }
      showNotificationToast(frame);
    },
    [mutePrefs],
  );

  const onError = useCallback(
    (frame: ErrorEventFrame, meta: PushFrameMeta) => {
      if (meta.replay) return; // live-only toast (see onNotification)
      // A muted origin suppresses the toast (errors have no kind to mute on).
      if (mutePrefs.mutedOrigins.includes(frame.service_origin)) return;
      showErrorToast(frame, resolveErrorMessage(frame.error_code, frame.message));
    },
    [mutePrefs],
  );

  usePushFrameSubscription({ onNotification, onError });
  return null;
}
